// Task modal — full edit/detail view per UX spec S8.
//
// Strategy: We keep a draft of all fields locally, PATCH on save. Inline
// title is committed on Enter/blur. Description has its own save button to
// avoid PATCH-spamming on every keystroke. Dependencies are managed with
// optimistic local state since the API has no GET-list endpoint yet.

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  Check,
  Diamond,
  GitMerge,
  Link2,
  Pencil,
  Sun,
  Trash2,
  X,
} from "lucide-preact";
import type {
  Phase,
  Task,
  TaskPriority,
  TaskUpdate,
} from "@juuplan/shared";
import {
  TASK_PRIORITIES,
} from "@juuplan/shared";
import {
  TASK_PRIORITY_LABEL,
  TASK_PRIORITY_VAR,
  TASK_STATUS_LABEL,
  TASK_STATUS_VAR,
  formatDateShort,
  softPillStyle,
} from "../lib/format";
import {
  addTaskDependency,
  createTask,
  deleteTask,
  fetchTaskDependencies,
  patchTask,
  removeTaskDependency,
  reorderTask,
} from "../store/project";
import { createReminder } from "../store/reminders";
import { createAutosave, type AutosaveStatus } from "../lib/autosave";
import { PendingReminders } from "./PendingReminders";
import { aiSuggestSubtasks, aiSummarizeTask, api, ApiError, listTaskGitLinks } from "../lib/api";
import { toastError, toastSuccess } from "../lib/toast";
import type { ApiTaskGitLink } from "@juuplan/shared";
import { useMe } from "../lib/auth";
import { addToMyDay } from "../store/myDay";
import { openProjectTaskSig } from "../store/deepLinks";
import { MergeTaskDialog } from "./MergeTaskDialog";
import { TitleField } from "./ui/TitleField";
import { findUser } from "../store/users";
import { useLabels, labelsById } from "../store/labels";
import { useStatuses } from "../store/statuses";
import { useCustomFields } from "../store/customFields";
import { useCycles } from "../store/cycles";
import { useWorkItemTypes } from "../store/workItemTypes";
import { Modal } from "./Modal";
import { Field, INPUT_CLS } from "./NewProjectModal";
import { CustomFieldsInputs } from "./CustomFieldsInputs";
import { MarkdownEditor } from "./MarkdownEditor";
import { AssigneePicker, AvatarStack } from "./AssigneePicker";
import { UpdatesFeed } from "./UpdatesFeed";
import { NotesSection } from "./NotesSection";
import { AttachmentsSection } from "./AttachmentsSection";
import { LabelPicker, LabelChipReadonly } from "./LabelPicker";
import { TaskGuestsSection } from "./TaskGuestsSection";
import { ChecklistSection } from "./ChecklistSection";
import { AiOriginBadge } from "./AiOriginBadge";
import { BotBadge } from "./BotBadge";
import { TimeTrackingSection } from "./TimeTrackingSection";
import { WatchToggle } from "./WatchToggle";
import { TaskWatchers } from "./TaskWatchers";

interface Props {
  task: Task;
  /** All other tasks in the project (used for dependency-picker). */
  siblings: Task[];
  /** Project id — required to load/create labels for the label-picker. */
  projectId: number;
  /**
   * Project phases — powers the "Phase" side-field so a task can be reassigned
   * to another phase directly from the detail view. Omitted → field hidden.
   */
  phases?: Phase[];
  onClose: () => void;
  /** Called after the task is deleted (so parent can drop its open-id). */
  onDeleted: () => void;
  /** Viewer-Rolle: alle Edit-Affordances ausgeblendet/deaktiviert. */
  readOnly?: boolean;
}

export function TaskModal(props: Props): preact.JSX.Element {
  const { task, siblings, projectId, phases, onClose, onDeleted, readOnly: readOnlyProp = false } =
    props;

  // Grabstein: diese Aufgabe wurde in eine andere zusammengeführt und ist
  // geschlossen. `readOnly` fasst die Viewer-Rolle UND den Grabstein-Zustand
  // zusammen — das schaltet "Bearbeiten", Beobachten und die neuen Aktionen
  // (Zusammenführen, Mein Tag) an jeder bestehenden Stelle im gleichen Zug ab,
  // ohne jede einzelne Gating-Stelle im File einzeln anzufassen.
  const isMerged = task.merged_into_task_id != null;
  const readOnly = readOnlyProp || isMerged;

  // Project custom statuses — used for the status control.
  const statusesState = useStatuses(projectId);
  const projectStatuses = statusesState.items;

  // Project cycles — used for the Zyklus picker.
  const cyclesState = useCycles(projectId);
  const projectCycles = cyclesState.items;

  // Project work item types — used for the Typ picker.
  const workItemTypesState = useWorkItemTypes(projectId);
  const projectWorkItemTypes = workItemTypesState.items;

  // Custom field definitions for this project.
  const customFieldsState = useCustomFields(projectId);
  const customFieldDefs = customFieldsState.items;

  // Project labels — used to render read-only label chips in the clean view.
  const labelsState = useLabels(projectId);

  // View vs. edit split: default is a clean read view; "Bearbeiten" reveals the
  // separate settings area. readOnly viewers never enter edit mode.
  const [mode, setMode] = useState<"view" | "edit">("view");
  const editing = mode === "edit" && !readOnly;

  // Draft state — title is committed on blur / Enter.
  const [title, setTitle] = useState(task.title);
  const [confirmDel, setConfirmDel] = useState(false);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [addingToMyDay, setAddingToMyDay] = useState(false);
  const isDone = task.statusInfo?.category === "done" || task.status === "done";

  // Description — autosaved (ticket #880: the explicit "Beschreibung
  // speichern" button is gone; see the autosave controller below). `descTaskId`
  // tracks which task the autosave controller currently targets, so a save
  // triggered by a debounce timer / in-flight retry that fires *after* the
  // user has switched to a different task still PATCHes the right one.
  const [description, setDescription] = useState(task.description ?? "");
  const [descStatus, setDescStatus] = useState<AutosaveStatus>("idle");
  const [descError, setDescError] = useState<unknown>(null);
  const descTaskIdRef = useRef<number | null>(null);
  const [descAutosave] = useState(() =>
    createAutosave(task.description ?? "", {
      debounceMs: 1000,
      save: (value) =>
        patchTask(descTaskIdRef.current as number, { description: value || null }),
      onStatusChange: (status, error) => {
        setDescStatus(status);
        setDescError(error);
      },
    })
  );

  // Dependencies — local mirror (no GET endpoint yet).
  const [deps, setDeps] = useState<number[]>([]);
  const [depPick, setDepPick] = useState("");

  // Sub-tasks — inline-add state.
  const [newSubTitle, setNewSubTitle] = useState("");
  const [addingSub, setAddingSub] = useState(false);
  const isSubTask = task.parent_id !== null;
  const subtasks = useMemo(
    () => siblings.filter((s) => s.parent_id === task.id),
    [siblings, task.id]
  );
  const submitSub = async (): Promise<void> => {
    const trimmed = newSubTitle.trim();
    if (!trimmed) return;
    setAddingSub(true);
    try {
      await createTask(task.phase_id, { title: trimmed, parent_id: task.id });
      setNewSubTitle("");
    } finally {
      setAddingSub(false);
    }
  };
  const toggleSubDone = async (sub: Task): Promise<void> => {
    // Resolve status_id by category: "done" when marking done, "backlog"/"unstarted"
    // otherwise. Falls back to first available status if no exact match.
    const isDone = sub.statusInfo?.category === "done" || sub.status === "done";
    const targetCategory = isDone ? "backlog" : "done";
    const found =
      projectStatuses.find((s) => s.category === targetCategory) ??
      projectStatuses.find((s) => s.category === (isDone ? "unstarted" : "done")) ??
      projectStatuses[0];
    if (found) {
      await patchTask(sub.id, { status_id: found.id });
    }
  };

  useEffect(() => {
    setTitle(task.title);

    const prevTaskId = descTaskIdRef.current;
    const nextDescription = task.description ?? "";
    if (prevTaskId === task.id) {
      // Same task re-rendering (e.g. a sibling refresh touched this task's
      // object identity) — never stomp a description the user may currently
      // be (auto)saving. Pitfall 3: a naive reset-on-[task.id] here would
      // revert in-progress edits whenever fresh server data for the SAME
      // task arrives.
      return;
    }
    if (prevTaskId === null) {
      // First mount — nothing to flush yet.
      descTaskIdRef.current = task.id;
      descAutosave.reset(nextDescription);
      setDescription(nextDescription);
      return;
    }
    // Genuinely switching to a DIFFERENT task: flush the outgoing task's
    // pending edit first (still targeting prevTaskId via descTaskIdRef),
    // THEN load the new task's text. Only after the flush settles do we
    // repoint the autosave controller at the new task and reset its
    // baseline — never before, or a debounced/in-flight save could land on
    // the wrong task.
    void descAutosave.flush().then(() => {
      if (descAutosave.getStatus() === "error") {
        // Best-effort: switching tasks is a user-initiated navigation, we
        // don't block it on a failed save, but the user must be told their
        // edit on the previous task did not persist.
        toastError(
          "Beschreibung der vorherigen Aufgabe konnte nicht gespeichert werden."
        );
      }
      descTaskIdRef.current = task.id;
      descAutosave.reset(nextDescription);
      setDescription(nextDescription);
    });
  }, [task.id]);

  // Defensive flush on unmount — covers any close path that doesn't route
  // through handleClose (there shouldn't be one, but this makes data loss
  // structurally hard to reintroduce later).
  useEffect(() => {
    return () => {
      void descAutosave.flush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDescriptionChange = (next: string): void => {
    setDescription(next);
    descAutosave.update(next);
  };

  const handleDescriptionBlur = (): void => {
    void descAutosave.flush().then(() => {
      if (descAutosave.getStatus() === "error") {
        toastError("Beschreibung konnte nicht gespeichert werden.");
      }
    });
  };

  const handleClose = (): void => {
    void descAutosave.flush().then(() => {
      if (descAutosave.getStatus() === "error") {
        toastError("Beschreibung konnte nicht gespeichert werden.");
      }
      onClose();
    });
  };

  // Hydrate existing dependencies from the backend (Phase 1.4 added
  // GET /api/tasks/:id/dependencies). Fire-and-forget — failures leave
  // the local list empty and the UI gracefully shows "no dependencies".
  useEffect(() => {
    let cancelled = false;
    void fetchTaskDependencies(task.id).then(
      (rows) => {
        if (cancelled) return;
        setDeps(rows.map((r) => r.depends_on_id));
      },
      () => {
        // ignore — leave deps empty
      }
    );
    return () => {
      cancelled = true;
    };
  }, [task.id]);

  // AI feature gate.
  const me = useMe();
  const aiEnabled = me.user?.ai_enabled === true && !isSubTask;

  // AI section state — ephemeral, cleared when modal closes (component unmounts).
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [aiSummaryBusy, setAiSummaryBusy] = useState(false);
  const [aiSummary503, setAiSummary503] = useState(false);

  const [aiSuggestions, setAiSuggestions] = useState<string[] | null>(null);
  const [aiSuggestBusy, setAiSuggestBusy] = useState(false);
  const [aiSuggest503, setAiSuggest503] = useState(false);

  // Track whether we're currently adopting a suggestion (keyed by index).
  const adoptingRef = useRef<Set<number>>(new Set());
  const [adoptingIdx, setAdoptingIdx] = useState<Set<number>>(new Set());

  const handleSummarize = async (): Promise<void> => {
    setAiSummaryBusy(true);
    setAiSummary503(false);
    try {
      const result = await aiSummarizeTask(task.id);
      setAiSummary(result.summary);
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setAiSummary503(true);
      }
      // Other errors: auto-toasted by api()
    } finally {
      setAiSummaryBusy(false);
    }
  };

  const handleSuggestSubtasks = async (): Promise<void> => {
    setAiSuggestBusy(true);
    setAiSuggest503(false);
    try {
      const result = await aiSuggestSubtasks(task.id);
      setAiSuggestions(result.suggestions.length > 0 ? result.suggestions : []);
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setAiSuggest503(true);
      }
      // Other errors: auto-toasted by api()
    } finally {
      setAiSuggestBusy(false);
    }
  };

  const adoptSuggestion = async (text: string, idx: number): Promise<void> => {
    if (adoptingRef.current.has(idx)) return;
    adoptingRef.current.add(idx);
    setAdoptingIdx(new Set(adoptingRef.current));
    try {
      // Temporarily set newSubTitle and call submitSub by directly creating the task.
      await createTask(task.phase_id, { title: text, parent_id: task.id });
      // Remove from suggestion list after successful adopt.
      setAiSuggestions((prev) => prev?.filter((_, i) => i !== idx) ?? null);
    } finally {
      adoptingRef.current.delete(idx);
      setAdoptingIdx(new Set(adoptingRef.current));
    }
  };

  const dismissSuggestion = (idx: number): void => {
    setAiSuggestions((prev) => prev?.filter((_, i) => i !== idx) ?? null);
  };

  // Git links — read-only, auto-populated by webhook receiver.
  const [gitLinks, setGitLinks] = useState<ApiTaskGitLink[]>([]);
  useEffect(() => {
    let cancelled = false;
    void listTaskGitLinks(task.id).then(
      (rows) => {
        if (cancelled) return;
        setGitLinks(rows);
      },
      () => {
        // ignore — leave list empty on failure
      }
    );
    return () => {
      cancelled = true;
    };
  }, [task.id]);

  // Recurrence state.
  type RecRule = "none" | "daily" | "weekly" | "monthly";
  const [recRule, setRecRule] = useState<RecRule>("none");
  const [recInterval, setRecInterval] = useState(1);

  useEffect(() => {
    void api<{ rule: string; interval_count: number } | null>(
      `/api/tasks/${task.id}/recurrence`,
      { silentError: true }
    )
      .then((r) => {
        if (r) {
          setRecRule(r.rule as RecRule);
          setRecInterval(r.interval_count);
        } else {
          setRecRule("none");
          setRecInterval(1);
        }
      })
      .catch(() => {});
  }, [task.id]);

  // Kein silentError: das Select zeigt den neuen Wert optimistisch — schlägt
  // der Server fehl, MUSS der Nutzer das erfahren (sonst glaubt er, die
  // Wiederholung sei gespeichert).
  const persistRecurrence = (rule: RecRule, interval: number): void => {
    const req =
      rule === "none"
        ? api(`/api/tasks/${task.id}/recurrence`, { method: "DELETE", silentError: true })
        : api(`/api/tasks/${task.id}/recurrence`, {
            method: "PUT",
            body: { rule, interval_count: interval, enabled: true },
            silentError: true,
          });
    void req.catch(() => {
      toastError("Wiederholung konnte nicht gespeichert werden.");
    });
  };

  // Reminder state.
  const [remindAt, setRemindAt] = useState("");

  // Patch a single field.
  const patch = (input: TaskUpdate): Promise<void> => patchTask(task.id, input);

  const commitTitle = async (): Promise<void> => {
    const next = title.trim();
    if (!next || next === task.title) {
      setTitle(task.title);
      return;
    }
    await patch({ title: next });
  };

  const onDelete = async (): Promise<void> => {
    await deleteTask(task.id);
    onDeleted();
  };

  const handleAddToMyDay = async (): Promise<void> => {
    setAddingToMyDay(true);
    try {
      await addToMyDay({ task_id: task.id });
      toastSuccess(`„${task.title}" zu „Mein Tag" hinzugefügt`);
    } catch {
      // addToMyDay -> api() hat den Fehler bereits getoastet.
    } finally {
      setAddingToMyDay(false);
    }
  };

  // Nach erfolgreichem Zusammenführen: Dialog zu, aktuelles Detail schliessen
  // und das ZIEL über den Deep-Link-Signal-Mechanismus öffnen (gleiches
  // Projekt garantiert server-seitig — siehe store/deepLinks.ts). refreshAll()
  // läuft schon in mergeTask()/mergePersonalTask(), also sind die Listen
  // dahinter längst aktuell, bevor das Ziel-Detail aufgeht.
  const onMerged = (target: { id: number }): void => {
    setShowMergeDialog(false);
    handleClose();
    openProjectTaskSig.value = target.id;
  };

  /**
   * Move the task to a different phase. Uses the cross-phase reorder endpoint
   * (PATCH /reorder) — `phase_id` is NOT part of the task PATCH schema. The
   * task is appended to the end of the target phase (sort_order = number of
   * top-level tasks already there). Sub-tasks are pinned to their parent's
   * phase server-side, so the control is disabled for them.
   */
  const moveToPhase = async (targetPhaseId: number): Promise<void> => {
    if (targetPhaseId === task.phase_id || isSubTask) return;
    const sortOrder = siblings.filter(
      (s) => s.phase_id === targetPhaseId && s.parent_id === null
    ).length;
    await reorderTask(task.id, { phase_id: targetPhaseId, sort_order: sortOrder });
  };

  const addDep = async (): Promise<void> => {
    if (!depPick) return;
    const id = Number(depPick);
    if (!Number.isFinite(id)) return;
    await addTaskDependency(task.id, id);
    setDeps((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setDepPick("");
  };

  const removeDep = async (id: number): Promise<void> => {
    await removeTaskDependency(task.id, id);
    setDeps((prev) => prev.filter((d) => d !== id));
  };

  const primaryAssignee = findUser(task.primary_assignee_id);

  const depCandidates = useMemo(
    () => siblings.filter((s) => s.id !== task.id && !deps.includes(s.id)),
    [siblings, task.id, deps]
  );

  // ---- shared controls (used in both the quick-edit bar and the edit area) ----

  const renderStatus = (): preact.JSX.Element => {
    // Use dynamic project statuses when available; fall back to legacy display.
    const useDynamic = projectStatuses.length > 0;
    const displayName = useDynamic
      ? (task.statusInfo?.name ?? TASK_STATUS_LABEL[task.status])
      : TASK_STATUS_LABEL[task.status];
    const displayColor = useDynamic
      ? (task.statusInfo?.color ?? TASK_STATUS_VAR[task.status])
      : TASK_STATUS_VAR[task.status];
    const selectValue = useDynamic && task.statusInfo
      ? String(task.statusInfo.id)
      : task.status;
    return (
      <div
        class={`juuplan-status-pill relative w-full rounded-md text-sm font-semibold min-h-[32px] ${
          readOnly ? "" : "hover:brightness-105 cursor-pointer"
        }`}
        style={softPillStyle(displayColor)}
      >
        {!readOnly && (
          <select
            value={selectValue}
            onChange={(e) => {
              const v = (e.target as HTMLSelectElement).value;
              if (useDynamic) {
                void patch({ status_id: Number(v) });
              } else {
                // Legacy fallback: resolve id from project statuses or skip.
                const found = projectStatuses.find((s) => s.category === v);
                if (found) void patch({ status_id: found.id });
              }
            }}
            class="absolute inset-0 w-full h-full opacity-0 cursor-pointer focus:outline-none"
            aria-label="Status"
          >
            {useDynamic
              ? projectStatuses.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {s.name}
                  </option>
                ))
              : (["backlog", "in_progress", "review", "done", "blocked"] as const).map((s) => (
                  <option key={s} value={s}>
                    {TASK_STATUS_LABEL[s]}
                  </option>
                ))}
          </select>
        )}
        <span class="flex items-center justify-center w-full h-full pointer-events-none px-3 py-1.5 select-none">
          {displayName}
        </span>
      </div>
    );
  };

  const renderPriority = (): preact.JSX.Element => (
    <div
      class={`juuplan-priority-pill relative w-full rounded-md text-sm font-semibold min-h-[32px] ${
        readOnly ? "" : "cursor-pointer"
      } ${
        task.priority
          ? readOnly ? "" : "hover:brightness-105"
          : `text-[color:var(--color-on-surface-variant)] border border-dashed border-[color:var(--color-outline-variant)] ${readOnly ? "" : "hover:bg-[color:var(--color-surface-container-low)]"}`
      }`}
      style={task.priority ? softPillStyle(TASK_PRIORITY_VAR[task.priority]) : undefined}
    >
      {!readOnly && (
        <select
          value={task.priority ?? ""}
          onChange={(e) => {
            const v = (e.target as HTMLSelectElement).value as TaskPriority | "";
            void patch({ priority: v === "" ? null : v });
          }}
          class="absolute inset-0 w-full h-full opacity-0 cursor-pointer focus:outline-none"
          aria-label="Priorität"
        >
          <option value="">—</option>
          {TASK_PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {TASK_PRIORITY_LABEL[p]}
            </option>
          ))}
        </select>
      )}
      <span class="flex items-center justify-center w-full h-full pointer-events-none px-3 py-1.5 select-none">
        {task.priority ? TASK_PRIORITY_LABEL[task.priority] : "—"}
      </span>
    </div>
  );

  const renderAssignees = (): preact.JSX.Element =>
    readOnly ? (
      task.assignee_ids.length > 0 ? (
        <AvatarStack userIds={task.assignee_ids} size="md" />
      ) : (
        <span class="text-xs text-[color:var(--color-on-surface-variant)]">
          — Nicht zugewiesen —
        </span>
      )
    ) : (
      <AssigneePicker
        value={task.assignee_ids}
        onChange={(ids) => void patch({ assignee_ids: ids })}
        label="Zuständige"
      />
    );

  // Read-only label chips for the clean view (editing happens in the edit area).
  const labelById = labelsById(labelsState.items);
  const viewLabelChips = task.label_ids.map((id) => {
    const l = labelById.get(id);
    return (
      <LabelChipReadonly
        key={id}
        name={l ? l.name : `#${id}`}
        // Ersatzfarbe für ein Label, dessen Id nicht mehr auflösbar ist.
        // Bleibt HEX und wird KEIN Token: `LabelChipReadonly` rechnet seine
        // Textfarbe per `pickFg()` aus der Luminanz — eine CSS-Variable kann
        // `pickFg` nicht lesen und fällt still auf Weiß zurück. Siehe die
        // benannte Ausnahme in components/__tests__/arbeitsflaecheDesignTokens.
        color={l ? l.color : "#737686"}
      />
    );
  });

  // Ersteller nur dann in der Meta-Zeile nennen, wenn es ein Bot war — bei
  // Menschen bleibt die Zeile unverändert knapp. Das KI-Badge in der
  // Kopfzeile meint etwas anderes (KI-Automation an der Aufgabe), beide
  // stehen daher bewusst an getrennten Stellen.
  const creator = findUser(task.created_by);

  // Teilbarer Direktlink auf diese Aufgabe: Empfänger landen über den
  // `?task=`-Parser in Project.tsx direkt im Detail, nicht nur im Projekt. Der
  // Pfad kommt aus projectId — den übergeben alle sechs Aufrufstellen (die fünf
  // Views plus der Deep-Link-Modal), der Button funktioniert damit überall.
  const copyDeepLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}/p/${projectId}?task=${task.id}`
      );
      toastSuccess("Link kopiert");
    } catch {
      toastError("Link konnte nicht kopiert werden.");
    }
  };

  return (
    <>
    <Modal
      title={task.title}
      onClose={handleClose}
      size="xl"
      placement="center"
      hideHeader
      footer={
        // Mobil kompakt: Metazeile einzeilig gekuerzt, Aktionen mit Kurzlabels —
        // vorher war die Fusszeile auf 390px drei Zeilen hoch und nahm dem
        // Inhalt (Abhaengigkeiten) den Platz.
        <div class="flex items-center justify-between gap-x-3 gap-y-1 flex-wrap text-micro text-[color:var(--color-on-surface-variant)]">
          <span class="min-w-0 max-w-full truncate sm:inline-flex sm:flex-wrap sm:items-center sm:gap-1 sm:whitespace-normal">
            Erstellt {formatDateShort(task.created_at)}
            {creator?.is_bot && (
              <>
                von {creator.name}
                <BotBadge ownerId={creator.bot_owner_id} />
              </>
            )}
            {" · "}Aktualisiert {formatDateShort(task.updated_at)}
            {task.completed_at && (
              <> · Erledigt {formatDateShort(task.completed_at)}</>
            )}
          </span>
          {isMerged ? null : readOnlyProp ? (
            <span class="inline-flex items-center gap-1 text-[color:var(--color-on-surface-variant)]">
              Nur-Lese-Zugriff
            </span>
          ) : confirmDel ? (
            <span class="inline-flex items-center gap-1.5">
              <button
                type="button"
                // Fokus auf die destruktive Bestätigung, sobald sie erscheint —
                // Tastatur-Nutzer müssen sonst erneut durchs Formular tabben.
                ref={(el) => el?.focus()}
                onClick={() => void onDelete()}
                class="rounded bg-[color:var(--color-error)] px-3 py-1.5 text-xs font-medium text-[color:var(--color-on-error)] hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-error)] focus-visible:ring-offset-2 transition-opacity"
              >
                In den Papierkorb verschieben
              </button>
              <button
                type="button"
                onClick={() => setConfirmDel(false)}
                class="rounded border border-[color:var(--color-outline-variant)] px-3 py-1.5 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] transition-colors"
              >
                Abbrechen
              </button>
            </span>
          ) : (
            <span class="inline-flex items-center gap-1.5">
              {!isDone && (
                <button
                  type="button"
                  onClick={() => void handleAddToMyDay()}
                  disabled={addingToMyDay}
                  class="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] hover:text-[color:var(--color-on-surface)] transition-colors disabled:opacity-50"
                >
                  <Sun size={14} aria-hidden="true" />
                  <span class="sm:hidden">Mein Tag</span>
                  <span class="hidden sm:inline">Zu „Mein Tag" hinzufügen</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowMergeDialog(true)}
                title="In eine andere Aufgabe zusammenführen — diese Aufgabe wird dabei geschlossen"
                class="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] hover:text-[color:var(--color-on-surface)] transition-colors"
              >
                <GitMerge size={14} aria-hidden="true" />
                Zusammenführen…
              </button>
              <button
                type="button"
                onClick={() => setConfirmDel(true)}
                class="inline-flex items-center gap-1 whitespace-nowrap rounded px-2 py-1 text-xs text-[color:var(--color-error)] hover:bg-[color:var(--color-error-container)] transition-colors"
              >
                <Trash2 size={14} aria-hidden="true" />
                <span class="sm:hidden">Papierkorb</span>
                <span class="hidden sm:inline">In den Papierkorb verschieben</span>
              </button>
            </span>
          )}
        </div>
      }
    >
      {/* Grabstein: die Aufgabe wurde in eine andere zusammengeführt — alle
          Inhalte hängen jetzt am Ziel, hier bleibt nur der Verweis. */}
      {isMerged && (
        <div class="mb-4 flex items-center gap-2 rounded-md border border-[color:var(--color-warning,#A16207)]/30 bg-[color:var(--color-warning,#A16207)]/8 px-3 py-2">
          <AlertTriangle
            size={16}
            class="shrink-0 text-[color:var(--color-warning,#A16207)]"
            aria-hidden="true"
          />
          <span class="flex-1 text-sm text-[color:var(--color-on-surface)]">
            Diese Aufgabe wurde in Aufgabe #{task.merged_into_task_id} zusammengeführt.
          </span>
          <button
            type="button"
            onClick={() => {
              handleClose();
              openProjectTaskSig.value = task.merged_into_task_id as number;
            }}
            class="shrink-0 rounded-md border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] transition-colors"
          >
            Ziel öffnen
          </button>
        </div>
      )}

      {/* Big inline title */}
      <header class="flex flex-wrap items-start gap-2 mb-4 pb-3 border-b border-[color:var(--color-outline-variant)]">
        <button
          type="button"
          onClick={handleClose}
          class="shrink-0 -ml-1 mt-1 inline-flex items-center justify-center rounded p-1.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
          aria-label="Zurück zur Übersicht"
          title="Zurück"
        >
          <ArrowLeft size={20} aria-hidden="true" />
        </button>
        {task.is_milestone && (
          <Diamond
            size={20}
            class="text-[color:var(--color-prio-urgent)] mt-1 shrink-0"
            fill="currentColor"
            aria-label="Meilenstein"
          />
        )}
        <span class="mt-1.5">
          <AiOriginBadge origin={task.ai_origin} size={14} />
        </span>
        {task.ticket && (
          <span
            class="mt-2 shrink-0 font-mono text-xs font-semibold text-[color:var(--color-on-surface-variant)]"
            title="Ticketnummer"
          >
            {task.ticket}
          </span>
        )}
        <TitleField
          value={title}
          readOnly={readOnly}
          onValue={setTitle}
          onBlur={() => !readOnly && void commitTitle()}
          onEnter={() => {
            if (!readOnly) void commitTitle();
          }}
          maxLength={200}
          label="Titel"
          class="flex-1 min-w-[min(100%,12rem)] text-xl leading-7 font-semibold text-[color:var(--color-on-surface)] tracking-tight text-balance bg-transparent rounded px-1 -mx-1 focus:outline-none focus:bg-[color:var(--color-surface-container-low)] focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors read-only:cursor-default"
        />
        {/* Aktionen als EIN Block: auf schmalen Fenstern bricht er gemeinsam in
            die naechste Zeile, statt dem Titel den Platz zu nehmen. */}
        <div class="ml-auto flex shrink-0 items-start gap-1">
        {/* Teilen bleibt auch im Nur-Lese-Modus verfügbar. Ohne gültige
            projectId gäbe es keinen auflösbaren Pfad — dann lieber kein Button
            als ein kaputter Link. */}
        {Number.isFinite(projectId) && projectId > 0 && (
          <button
            type="button"
            onClick={() => void copyDeepLink()}
            class="shrink-0 mt-0.5 inline-flex items-center justify-center rounded p-1.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
            aria-label="Link zur Aufgabe kopieren"
            title="Link kopieren"
          >
            <Link2 size={16} aria-hidden="true" />
          </button>
        )}
        {!readOnly && <WatchToggle taskId={task.id} />}
        {!readOnly && (
          <button
            type="button"
            onClick={() => setMode((m) => (m === "edit" ? "view" : "edit"))}
            class={`shrink-0 mt-0.5 inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 ${
              editing
                ? "bg-[color:var(--color-primary)] text-[color:var(--color-on-primary)] hover:bg-[color:var(--color-primary-strong)]"
                : "border border-[color:var(--color-outline-variant)] text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)]"
            }`}
            aria-pressed={editing}
          >
            {editing ? (
              <>
                <Check size={14} aria-hidden="true" />
                Fertig
              </>
            ) : (
              <>
                <Pencil size={14} aria-hidden="true" />
                Bearbeiten
              </>
            )}
          </button>
        )}
        </div>
      </header>

      <div class="flex flex-col gap-6">
        {/* Quick-edit bar — core fields stay editable in the clean view. */}
        {!editing && (
          <div class="flex flex-wrap gap-x-5 gap-y-3">
            <SideField label="Status">
              <div class="w-40">{renderStatus()}</div>
            </SideField>
            <SideField label="Priorität">
              <div class="w-36">{renderPriority()}</div>
            </SideField>
            <SideField label="Zuständig">{renderAssignees()}</SideField>
            {/* #854: Beobachter bekommen die Status- und Kommentar-Benachrichtigungen
                der Aufgabe. Bewusst direkt neben „Zuständig" — die Frage „wer muss
                das mitbekommen?" stellt sich zusammen mit „wer macht es?". */}
            <SideField label="Beobachter">
              <div class="w-56">
                <TaskWatchers taskId={task.id} readOnly={readOnly} />
              </div>
            </SideField>
            <SideField label="Fällig">
              <span class="inline-flex items-center gap-1 text-sm tabular-nums text-[color:var(--color-on-surface)] min-h-[32px]">
                <Calendar
                  size={14}
                  class="text-[color:var(--color-on-surface-variant)]"
                  aria-hidden="true"
                />
                {task.deadline ? (
                  formatDateShort(task.deadline)
                ) : (
                  <span class="text-[color:var(--color-on-surface-variant)]">—</span>
                )}
              </span>
            </SideField>
            {viewLabelChips.length > 0 && (
              <SideField label="Labels">
                <div class="flex flex-wrap items-center gap-1 min-h-[32px]">
                  {viewLabelChips}
                </div>
              </SideField>
            )}
          </div>
        )}

        {/* Content (clean view) */}
        {!editing && (
        <section class="flex flex-col gap-5 min-w-0">
          <Field label="Beschreibung" htmlFor="tm-desc">
            {readOnly ? (
              <p class="text-sm text-[color:var(--color-on-surface)] whitespace-pre-wrap min-h-[1.5rem]">
                {description.trim() || (
                  <span class="italic text-[color:var(--color-on-surface-variant)]">
                    Keine Beschreibung
                  </span>
                )}
              </p>
            ) : (
              // No explicit save button (ticket #880) — description autosaves via
              // `descAutosave` (debounced update + flush on blur/close/task-switch).
              // `onFocusOut` (not `onBlur` on the textarea itself, which
              // MarkdownEditor doesn't expose) bubbles natively from the
              // textarea through this wrapper, so a flush fires whenever
              // focus leaves the editor — including moving it to the
              // toolbar buttons, which is harmless (flush is a no-op when
              // nothing is dirty).
              <div onFocusOut={handleDescriptionBlur}>
                <MarkdownEditor
                  id="tm-desc"
                  value={description}
                  onChange={handleDescriptionChange}
                  rows={8}
                  placeholder="Beschreibung in Markdown…"
                />
                {/* Fixed-size status slot, always rendered — reserves its
                    space even when idle so appearing/disappearing text never
                    shifts the editor (pitfall: ~18px CLS from flex-wrap). */}
                <div class="flex h-5 items-center justify-end gap-2 whitespace-nowrap text-micro">
                  {descStatus === "saving" && (
                    <span class="text-[color:var(--color-on-surface-variant)]">
                      Speichert…
                    </span>
                  )}
                  {descStatus === "saved" && (
                    <span class="text-[color:var(--color-on-surface-variant)]">
                      Gespeichert
                    </span>
                  )}
                  {descStatus === "error" && (
                    <span class="inline-flex items-center gap-1.5 text-[color:var(--color-error)]">
                      Speichern fehlgeschlagen
                      <button
                        type="button"
                        onClick={() => void descAutosave.flush()}
                        class="underline underline-offset-2 hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-error)] focus-visible:ring-offset-1 rounded"
                        title={descError instanceof Error ? descError.message : undefined}
                      >
                        Erneut versuchen
                      </button>
                    </span>
                  )}
                </div>
              </div>
            )}
          </Field>

          {customFieldDefs.length > 0 && (
            <CustomFieldsInputs
              taskId={task.id}
              customValues={task.custom_values ?? {}}
              fieldDefs={customFieldDefs}
              readOnly={readOnly}
            />
          )}

          <section>
            <h3 class="text-xs font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-2">
              Abhängigkeiten
            </h3>
            {deps.length === 0 ? (
              <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
                Keine Abhängigkeiten
              </p>
            ) : (
              <ul class="flex flex-col gap-1 mb-2">
                {deps.map((id) => {
                  const t = siblings.find((s) => s.id === id);
                  return (
                    <li
                      key={id}
                      class="flex items-center justify-between gap-2 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2.5 py-1.5 text-xs"
                    >
                      <span class="truncate">
                        {t ? t.title : `Aufgabe #${id}`}
                      </span>
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => void removeDep(id)}
                          class="rounded p-1 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-error-container)] hover:text-[color:var(--color-on-error-container)] transition-colors"
                          aria-label="Abhängigkeit entfernen"
                        >
                          <Trash2 size={14} aria-hidden="true" />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {!readOnly && (
              <div class="flex items-center gap-2">
                <select
                  value={depPick}
                  aria-label="Abhängigkeit wählen"
                  onChange={(e) =>
                    setDepPick((e.target as HTMLSelectElement).value)
                  }
                  class={INPUT_CLS}
                >
                  <option value="">Aufgabe wählen…</option>
                  {depCandidates.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => void addDep()}
                  disabled={!depPick}
                  class="rounded border border-[color:var(--color-outline-variant)] px-3 py-2 text-xs font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] disabled:opacity-50 transition-colors min-h-[32px]"
                >
                  Hinzufügen
                </button>
              </div>
            )}
          </section>

          {!isSubTask && (
            <section>
              <h3 class="text-xs font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-2 flex items-center gap-2">
                Sub-Aufgaben
                {subtasks.length > 0 && (
                  <span class="text-label font-medium normal-case tracking-normal text-[color:var(--color-on-surface-variant)]">
                    {subtasks.filter((s) => s.statusInfo?.category === "done" || s.status === "done").length}/{subtasks.length} erledigt
                  </span>
                )}
              </h3>
              {subtasks.length === 0 ? (
                <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
                  Noch keine Sub-Aufgaben
                </p>
              ) : (
                <ul class="flex flex-col gap-1 mb-2">
                  {subtasks.map((s) => (
                    <li
                      key={s.id}
                      class="flex items-center gap-2 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2.5 py-1.5 text-xs"
                    >
                      <input
                        type="checkbox"
                        checked={s.statusInfo?.category === "done" || s.status === "done"}
                        disabled={readOnly}
                        onChange={() => void toggleSubDone(s)}
                        class="accent-[color:var(--color-primary)] disabled:opacity-60"
                        aria-label={`Sub-Aufgabe ${s.title} als erledigt markieren`}
                      />
                      <span
                        class={`flex-1 truncate ${s.statusInfo?.category === "done" || s.status === "done" ? "line-through opacity-60" : ""}`}
                      >
                        {s.title}
                      </span>
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => void deleteTask(s.id)}
                          class="rounded p-1 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-error-container)] hover:text-[color:var(--color-on-error-container)] transition-colors"
                          aria-label="Sub-Aufgabe in den Papierkorb verschieben"
                        >
                          <Trash2 size={14} aria-hidden="true" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {!readOnly && (
                <div class="flex items-center gap-2">
                  <input
                    type="text"
                    value={newSubTitle}
                    onInput={(e) => setNewSubTitle((e.target as HTMLInputElement).value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        void submitSub();
                      }
                    }}
                    placeholder="Neue Sub-Aufgabe…"
                    class={INPUT_CLS}
                    aria-label="Neue Sub-Aufgabe"
                  />
                  <button
                    type="button"
                    onClick={() => void submitSub()}
                    disabled={!newSubTitle.trim() || addingSub}
                    class="rounded border border-[color:var(--color-outline-variant)] px-3 py-2 text-xs font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] disabled:opacity-50 transition-colors min-h-[32px]"
                  >
                    {addingSub ? "Lege an…" : "Hinzufügen"}
                  </button>
                </div>
              )}
            </section>
          )}

          {isSubTask && (
            <p class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-3 py-2 text-xs text-[color:var(--color-on-surface-variant)]">
              Diese Aufgabe ist eine Sub-Aufgabe. Sub-Aufgaben können selbst keine weiteren Sub-Aufgaben haben.
            </p>
          )}

          {/* P2: lightweight checklist (distinct from sub-tasks). */}
          <section>
            <ChecklistSection taskId={task.id} readOnly={readOnly} />
          </section>

          {/* P3: time tracking — timer + manual entries vs. estimate. */}
          <section>
            <TimeTrackingSection taskId={task.id} estimatedHours={task.estimated_hours} readOnly={readOnly} />
          </section>

          {aiEnabled && (
            <section data-testid="ai-section">
              <h3 class="text-xs font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-2">
                KI-Assistent
              </h3>

              {/* Summarize */}
              <div class="mb-3">
                <button
                  type="button"
                  onClick={() => void handleSummarize()}
                  disabled={aiSummaryBusy}
                  class="rounded border border-[color:var(--color-outline-variant)] px-3 py-2 text-xs font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] disabled:opacity-50 transition-colors min-h-[32px]"
                >
                  {aiSummaryBusy ? "Zusammenfassung wird erstellt…" : "Zusammenfassung erstellen"}
                </button>
                {aiSummary503 && (
                  <p class="mt-1 text-xs text-[color:var(--color-on-surface-variant)] italic">
                    KI ist derzeit deaktiviert.
                  </p>
                )}
                {aiSummary && (
                  <div class="mt-2 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-3 py-2">
                    <p class="text-xs text-[color:var(--color-on-surface)] whitespace-pre-wrap" data-testid="ai-summary-text">
                      {aiSummary}
                    </p>
                    <button
                      type="button"
                      onClick={() => void navigator.clipboard.writeText(aiSummary)}
                      class="mt-2 rounded border border-[color:var(--color-outline-variant)] px-2 py-1 text-label font-medium text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container)] transition-colors"
                    >
                      Kopieren
                    </button>
                  </div>
                )}
              </div>

              {/* Suggest subtasks */}
              <div>
                <button
                  type="button"
                  onClick={() => void handleSuggestSubtasks()}
                  disabled={aiSuggestBusy}
                  class="rounded border border-[color:var(--color-outline-variant)] px-3 py-2 text-xs font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] disabled:opacity-50 transition-colors min-h-[32px]"
                >
                  {aiSuggestBusy ? "Vorschläge werden geladen…" : "Sub-Aufgaben vorschlagen"}
                </button>
                {aiSuggest503 && (
                  <p class="mt-1 text-xs text-[color:var(--color-on-surface-variant)] italic">
                    KI ist derzeit deaktiviert.
                  </p>
                )}
                {aiSuggestions !== null && aiSuggestions.length === 0 && (
                  <p class="mt-1 text-xs text-[color:var(--color-on-surface-variant)] italic">
                    Keine Vorschläge vorhanden.
                  </p>
                )}
                {aiSuggestions !== null && aiSuggestions.length > 0 && (
                  <ul class="mt-2 flex flex-col gap-1" data-testid="ai-suggestions-list">
                    {aiSuggestions.map((s, i) => (
                      <li
                        key={i}
                        class="flex items-center gap-2 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2.5 py-1.5 text-xs"
                      >
                        <span class="flex-1 truncate">{s}</span>
                        {!readOnly && (
                          <button
                            type="button"
                            onClick={() => void adoptSuggestion(s, i)}
                            disabled={adoptingIdx.has(i)}
                            class="rounded border border-[color:var(--color-outline-variant)] px-2 py-1 text-label font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container)] disabled:opacity-50 transition-colors"
                            data-testid={`ai-adopt-${i}`}
                          >
                            {adoptingIdx.has(i) ? "…" : "Übernehmen"}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => dismissSuggestion(i)}
                          class="rounded p-1 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-error-container)] hover:text-[color:var(--color-on-error-container)] transition-colors"
                          aria-label={`Vorschlag ${i + 1} verwerfen`}
                          data-testid={`ai-dismiss-${i}`}
                        >
                          <X size={14} aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          )}

          <AttachmentsSection taskId={task.id} />

          <GitActivitySection links={gitLinks} />

          <UpdatesFeed taskId={task.id} />

          <NotesSection taskId={task.id} readOnly={readOnly} />

          {!readOnly && <TaskGuestsSection taskId={task.id} />}
        </section>
        )}

        {/* Separate settings / edit area — all configuration lives here. */}
        {editing && (
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
          {phases && phases.length > 0 && (
            <SideField label="Phase">
              {readOnly || isSubTask ? (
                <span class="text-sm text-[color:var(--color-on-surface)]">
                  {phases.find((p) => p.id === task.phase_id)?.name ?? (
                    <span class="text-[color:var(--color-on-surface-variant)]">—</span>
                  )}
                </span>
              ) : (
                <select
                  value={String(task.phase_id)}
                  onChange={(e) => {
                    const v = Number((e.target as HTMLSelectElement).value);
                    if (Number.isFinite(v)) void moveToPhase(v);
                  }}
                  class={INPUT_CLS}
                  aria-label="Phase"
                >
                  {phases.map((p) => (
                    <option key={p.id} value={String(p.id)}>
                      {p.name}
                    </option>
                  ))}
                </select>
              )}
              {isSubTask && !readOnly && (
                <p class="mt-1 text-label text-[color:var(--color-on-surface-variant)]">
                  Sub-Aufgaben folgen der Phase ihrer übergeordneten Aufgabe.
                </p>
              )}
            </SideField>
          )}

          <SideField label="Status">{renderStatus()}</SideField>

          <SideField label="Priorität">{renderPriority()}</SideField>

          <SideField label="Zuständig">
            {renderAssignees()}
            {primaryAssignee && (
              <p class="mt-1 text-label text-[color:var(--color-on-surface-variant)]">
                {/* Bots: der Name statt der .invalid-Adresse — die traegt keine
                    Information und verdraengt nur die eigentliche Identitaet. */}
                Primary:{" "}
                {primaryAssignee.is_bot ? primaryAssignee.name : primaryAssignee.email}
              </p>
            )}
          </SideField>

          <SideField label="Beobachter">
            <TaskWatchers taskId={task.id} readOnly={readOnly} />
            <p class="mt-1 text-label text-[color:var(--color-on-surface-variant)]">
              Erhalten Status- und Kommentar-Benachrichtigungen.
            </p>
          </SideField>

          <SideField label="Labels" class="sm:col-span-2">
            {readOnly ? (
              <div class="pointer-events-none opacity-90" aria-disabled="true">
                <LabelPicker task={task} projectId={projectId} />
              </div>
            ) : (
              <LabelPicker task={task} projectId={projectId} />
            )}
          </SideField>

          <SideField label="Startdatum">
            <DateInput
              value={task.start_date}
              onChange={(v) => void patch({ start_date: v })}
              ariaLabel="Startdatum"
              readOnly={readOnly}
            />
          </SideField>

          <SideField label="Enddatum">
            <DateInput
              value={task.end_date}
              onChange={(v) => void patch({ end_date: v })}
              ariaLabel="Enddatum"
              readOnly={readOnly}
            />
          </SideField>

          <SideField label="Deadline">
            <DateInput
              value={task.deadline}
              onChange={(v) => void patch({ deadline: v })}
              ariaLabel="Deadline"
              readOnly={readOnly}
            />
          </SideField>

          <SideField label="Wiederholung">
            {readOnly ? (
              <span class="text-sm text-[color:var(--color-on-surface-variant)]">
                {recRule === "none"
                  ? "—"
                  : recRule === "daily"
                  ? "Täglich"
                  : recRule === "weekly"
                  ? "Wöchentlich"
                  : "Monatlich"}
              </span>
            ) : (
              <div class="flex flex-col gap-1.5">
                <select
                  value={recRule}
                  onChange={(e) => {
                    const v = (e.target as HTMLSelectElement).value as RecRule;
                    setRecRule(v);
                    persistRecurrence(v, recInterval);
                  }}
                  class={INPUT_CLS}
                  aria-label="Wiederholung"
                >
                  <option value="none">Keine</option>
                  <option value="daily">Täglich</option>
                  <option value="weekly">Wöchentlich</option>
                  <option value="monthly">Monatlich</option>
                </select>
                {recRule !== "none" && (
                  <div class="flex items-center gap-1.5">
                    <input
                      type="number"
                      min={1}
                      value={recInterval}
                      onChange={(e) => {
                        const v = Math.max(1, Number((e.target as HTMLInputElement).value));
                        setRecInterval(v);
                        persistRecurrence(recRule, v);
                      }}
                      class={`${INPUT_CLS} w-16`}
                      aria-label="Intervall"
                    />
                    <span class="text-xs text-[color:var(--color-on-surface-variant)]">× Intervall</span>
                  </div>
                )}
              </div>
            )}
          </SideField>

          {!readOnly && (
            <SideField label="Erinnerung" class="sm:col-span-2">
              <div class="flex flex-col gap-1.5">
                <input
                  type="datetime-local"
                  value={remindAt}
                  onChange={(e) => setRemindAt((e.target as HTMLInputElement).value)}
                  class={INPUT_CLS}
                  aria-label="Erinnerung Datum und Uhrzeit"
                />
                <button
                  type="button"
                  disabled={!remindAt}
                  onClick={async () => {
                    if (!remindAt) return;
                    await createReminder({
                      task_id: task.id,
                      remind_at: new Date(remindAt).toISOString(),
                    });
                    toastSuccess("Erinnerung gesetzt.");
                    setRemindAt("");
                  }}
                  class="rounded border border-[color:var(--color-outline-variant)] px-3 py-1.5 text-xs font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] disabled:opacity-50 transition-colors"
                >
                  Setzen
                </button>
                <PendingReminders taskId={task.id} />
              </div>
            </SideField>
          )}

          <SideField label="Meilenstein" class="sm:col-span-2">
            <label class="inline-flex items-center gap-2 text-sm text-[color:var(--color-on-surface)]">
              <input
                type="checkbox"
                checked={task.is_milestone}
                disabled={readOnly}
                onChange={(e) =>
                  void patch({
                    is_milestone: (e.target as HTMLInputElement).checked,
                  })
                }
                class="size-4 rounded border-[color:var(--color-outline)] text-[color:var(--color-primary)] focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 disabled:opacity-60"
              />
              Diese Aufgabe ist ein Meilenstein
            </label>
          </SideField>

          <SideField label="Zyklus">
            {readOnly ? (
              <span class="text-sm text-[color:var(--color-on-surface)]">
                {task.cycle_id
                  ? (projectCycles.find((c) => c.id === task.cycle_id)?.name ?? `Zyklus #${task.cycle_id}`)
                  : <span class="text-[color:var(--color-on-surface-variant)]">—</span>}
              </span>
            ) : (
              <select
                value={task.cycle_id ?? ""}
                onChange={(e) => {
                  const v = (e.target as HTMLSelectElement).value;
                  void patch({ cycle_id: v === "" ? null : Number(v) });
                }}
                class={INPUT_CLS}
                aria-label="Zyklus"
              >
                <option value="">Kein Zyklus</option>
                {projectCycles.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
          </SideField>

          <SideField label="Typ">
            {readOnly ? (
              <span class="text-sm text-[color:var(--color-on-surface)]">
                {task.type_id
                  ? (projectWorkItemTypes.find((t) => t.id === task.type_id)?.name ?? `Typ #${task.type_id}`)
                  : <span class="text-[color:var(--color-on-surface-variant)]">—</span>}
              </span>
            ) : (
              <select
                value={task.type_id != null ? String(task.type_id) : ""}
                onChange={(e) => {
                  const v = (e.target as HTMLSelectElement).value;
                  if (v !== "") void patch({ type_id: Number(v) });
                }}
                class={INPUT_CLS}
                aria-label="Typ"
              >
                {task.type_id == null && (
                  <option value="" disabled>
                    Typ wählen…
                  </option>
                )}
                {projectWorkItemTypes.map((t) => (
                  <option key={t.id} value={String(t.id)}>
                    {t.name}
                  </option>
                ))}
              </select>
            )}
          </SideField>

          <div class="grid grid-cols-2 gap-2 sm:col-span-2">
            <SideField label="Geschätzt (h)">
              <NumberInput
                value={task.estimated_hours}
                onChange={(v) => void patch({ estimated_hours: v })}
                ariaLabel="Geschätzte Stunden"
                readOnly={readOnly}
              />
            </SideField>
            <SideField label="Tatsächlich (h)">
              <NumberInput
                value={task.actual_hours}
                onChange={(v) => void patch({ actual_hours: v })}
                ariaLabel="Tatsächliche Stunden"
                readOnly={readOnly}
              />
            </SideField>
          </div>
        </div>
        )}
      </div>
    </Modal>
    {showMergeDialog && (
      <MergeTaskDialog
        kind="task"
        source={{ id: task.id, title: task.title, project_id: projectId }}
        onDone={onMerged}
        onClose={() => setShowMergeDialog(false)}
      />
    )}
    </>
  );
}

// ---------- helpers ----------

function SideField(props: {
  label: string;
  children: preact.ComponentChildren;
  class?: string;
}): preact.JSX.Element {
  return (
    <div class={`flex flex-col gap-1 ${props.class ?? ""}`}>
      <span class="text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
        {props.label}
      </span>
      {props.children}
    </div>
  );
}

// Datumsfeld im Modal. Anders als in der Tabelle bleibt der Browser-Platzhalter
// stehen — hier ist genau EIN Feld sichtbar, und „tt.mm.jjjj" ist im Formular
// ein nützlicher Formathinweis statt Rauschen. Weg muss nur das native
// Kalendersymbol: links steht schon ein Lucide-`Calendar`, rechts malte der
// Browser ein zweites in einem anderen Formvokabular.
function DateInput(props: {
  value: string | null;
  onChange: (v: string | null) => void;
  ariaLabel: string;
  readOnly?: boolean;
}): preact.JSX.Element {
  return (
    <div class="relative">
      <input
        type="date"
        value={props.value ?? ""}
        readOnly={props.readOnly}
        disabled={props.readOnly}
        onChange={(e) => {
          const v = (e.target as HTMLInputElement).value;
          props.onChange(v || null);
        }}
        class={`${INPUT_CLS} pl-8 disabled:opacity-70 disabled:cursor-default [&::-webkit-calendar-picker-indicator]:opacity-0 [&:hover::-webkit-calendar-picker-indicator]:opacity-60 [&:focus::-webkit-calendar-picker-indicator]:opacity-100`}
        aria-label={props.ariaLabel}
      />
      <Calendar
        size={14}
        class="absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--color-on-surface-variant)] pointer-events-none"
        aria-hidden="true"
      />
    </div>
  );
}

function NumberInput(props: {
  value: string | null;
  onChange: (v: string | null) => void;
  ariaLabel: string;
  readOnly?: boolean;
}): preact.JSX.Element {
  return (
    <input
      type="number"
      min="0"
      step="0.25"
      value={props.value ?? ""}
      readOnly={props.readOnly}
      disabled={props.readOnly}
      onChange={(e) => {
        const v = (e.target as HTMLInputElement).value;
        props.onChange(v === "" ? null : v);
      }}
      class={`${INPUT_CLS} disabled:opacity-70 disabled:cursor-default`}
      aria-label={props.ariaLabel}
    />
  );
}

// Label map for git link types.
const GIT_TYPE_LABEL: Record<ApiTaskGitLink["type"], string> = {
  commit: "Commits",
  branch: "Branches",
  pr: "Pull Requests",
};

/**
 * Read-only Git-Aktivität section — renders linked commits, branches, and
 * pull requests grouped by type, with external links and state badges.
 */
function GitActivitySection(props: {
  links: ApiTaskGitLink[];
}): preact.JSX.Element {
  const { links } = props;

  // Group links by type in a deterministic order.
  const order: ApiTaskGitLink["type"][] = ["commit", "branch", "pr"];
  const groups = order
    .map((type) => ({ type, items: links.filter((l) => l.type === type) }))
    .filter((g) => g.items.length > 0);

  return (
    <section>
      <h3 class="text-xs font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-2">
        Git-Aktivität
      </h3>
      {links.length === 0 ? (
        <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
          Keine verknüpfte Git-Aktivität
        </p>
      ) : (
        <div class="flex flex-col gap-3">
          {groups.map(({ type, items }) => (
            <div key={type}>
              <p class="text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-1">
                {GIT_TYPE_LABEL[type]}
              </p>
              <ul class="flex flex-col gap-1">
                {items.map((link) => (
                  <li
                    key={link.id}
                    class="flex items-center justify-between gap-2 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2.5 py-1.5 text-xs"
                  >
                    <span class="truncate font-mono">
                      {link.url ? (
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          class="text-[color:var(--color-primary)] hover:underline"
                        >
                          {link.external_ref}
                        </a>
                      ) : (
                        link.external_ref
                      )}
                    </span>
                    {link.state && (
                      <span class="shrink-0 rounded-full bg-[color:var(--color-surface-container)] border border-[color:var(--color-outline-variant)] px-2 py-0.5 text-label font-medium text-[color:var(--color-on-surface-variant)] capitalize">
                        {link.state}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
